import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Link from '@cloudscape-design/components/link';
import Select from '@cloudscape-design/components/select';
import TextFilter from '@cloudscape-design/components/text-filter';
import Pagination from '@cloudscape-design/components/pagination';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Alert from '@cloudscape-design/components/alert';
import { listStores, updateStore, deleteStore } from '../api/stores';
import { getCredentials } from '../api/client';
import { formatTimestamp, getStoredTimeFormat } from '../utils/timeFormat';

interface StoreItem {
  name: string;
  domain?: string;
  createdAt: string;
  updatedAt?: string;
  status: string;
  deletionProtection?: boolean;
  streamKey?: { fields: string[]; delimiter?: string };
  type?: string;
}

export function StoreList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [filterText, setFilterText] = useState('');
  const [sortingColumn, setSortingColumn] = useState<any>({ sortingField: 'createdAt' });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [visibleColumns, setVisibleColumns] = useState(['name', 'domain', 'createdAt', 'updatedAt', 'deletionProtection', 'status']);
  const [selectedItems, setSelectedItems] = useState<StoreItem[]>([]);
  const [deleting, setDeleting] = useState(false);
  const pageTokens = useRef<(string | undefined)[]>([undefined]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['stores', currentPageIndex, pageSize, statusFilter],
    queryFn: () => listStores({ limit: pageSize, nextToken: pageTokens.current[currentPageIndex - 1], status: statusFilter !== 'ALL' ? statusFilter : undefined }),
    enabled: !!getCredentials(),
  });

  const responseData = data?.data as { stores?: StoreItem[]; nextToken?: string } | null;
  const stores: StoreItem[] = responseData?.stores || [];
  const nextToken = responseData?.nextToken;

  // Track tokens for pages we've seen
  if (nextToken && pageTokens.current.length <= currentPageIndex) {
    pageTokens.current[currentPageIndex] = nextToken;
  }

  const filteredStores = stores
    .filter((s) => s.type !== 'VIEW')
    .filter((s) => {
      if (statusFilter === 'ALL') return true;
      return s.status === statusFilter;
    })
    .filter((s) =>
      s.name.toLowerCase().includes(filterText.toLowerCase()) ||
      (s.domain || '').toLowerCase().includes(filterText.toLowerCase())
    );

  const sortedStores = [...filteredStores].sort((a, b) => {
    if (!sortingColumn) return 0;
    const field = sortingColumn.sortingField as keyof StoreItem;
    const aVal = (a[field] as string) || '';
    const bVal = (b[field] as string) || '';
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortingDescending ? -cmp : cmp;
  });

  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'DELETED', label: 'Deleted' },
    { value: 'ALL', label: 'All statuses' },
  ];

  const handleDisableProtection = async () => {
    if (!selectedItems.length) return;
    setDeleting(true);
    for (const store of selectedItems) {
      try {
        await updateStore(store.name, { deletionProtection: false });
      } catch (e) {
        console.error(`Failed to disable protection on ${store.name}:`, e);
      }
    }
    setSelectedItems([]);
    setDeleting(false);
    window.location.reload();
  };

  const handleBatchDelete = async () => {
    const deletable = selectedItems.filter(s => !s.deletionProtection);
    if (!deletable.length) {
      alert('Selected stores still have deletion protection enabled. Disable protection first.');
      return;
    }
    const confirmed = window.confirm(`Delete ${deletable.length} store(s)?`);
    if (!confirmed) return;
    setDeleting(true);
    for (const store of deletable) {
      try {
        await deleteStore(store.name);
      } catch (e) {
        console.error(`Failed to delete ${store.name}:`, e);
      }
    }
    setSelectedItems([]);
    setDeleting(false);
    window.location.reload();
  };

  return (
    <SpaceBetween size="l">
      {isError && <Alert type="error">Failed to load stores. Check your credentials and try again.</Alert>}
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              disabled={selectedItems.length === 0 || deleting}
              loading={deleting}
              onClick={handleDisableProtection}
            >
              Disable protection
            </Button>
            <Button
              disabled={selectedItems.length === 0 || deleting}
              loading={deleting}
              onClick={handleBatchDelete}
            >
              Delete
            </Button>
            <Button variant="primary" onClick={() => navigate('/stores/create')}>
              Create event store
            </Button>
          </SpaceBetween>
        }
      >
        Event stores ({(currentPageIndex - 1) * pageSize + filteredStores.length}{nextToken ? '+' : ''})
      </Header>
      <Table
        loading={isLoading}
        loadingText="Loading stores"
        items={sortedStores}
        selectionType="multi"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as StoreItem[])}
        trackBy="name"
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending ?? false);
        }}
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <TextFilter
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              filteringPlaceholder="Find stores"
            />
            <Select
              selectedOption={statusOptions.find(o => o.value === statusFilter)!}
              onChange={({ detail }) => {
                setStatusFilter(detail.selectedOption.value!);
                setCurrentPageIndex(1);
                pageTokens.current = [undefined];
              }}
              options={statusOptions}
            />
          </SpaceBetween>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            sortingField: 'name',
            cell: (item: StoreItem) => (
              <Link
                href={`/stores/${item.name}`}
                onFollow={(event) => {
                  event.preventDefault();
                  navigate(`/stores/${item.name}`);
                }}
              >
                <span style={item.status === 'DELETED' ? { opacity: 0.5 } : undefined}>
                  {item.name}
                </span>
              </Link>
            ),
          },
          {
            id: 'domain',
            header: 'Domain',
            sortingField: 'domain',
            cell: (item: StoreItem) => item.domain || '—',
          },
          {
            id: 'createdAt',
            header: 'Created',
            sortingField: 'createdAt',
            cell: (item: StoreItem) => formatTimestamp(item.createdAt, getStoredTimeFormat()),
          },
          {
            id: 'updatedAt',
            header: 'Updated',
            sortingField: 'updatedAt',
            cell: (item: StoreItem) => item.updatedAt ? formatTimestamp(item.updatedAt, getStoredTimeFormat()) : '—',
          },
          {
            id: 'deletionProtection',
            header: 'Deletion protection',
            sortingField: 'deletionProtection',
            cell: (item: StoreItem) => item.deletionProtection ? 'Enabled' : 'Disabled',
          },
          {
            id: 'streamKey',
            header: 'Stream key',
            cell: (item: StoreItem) => item.streamKey ? item.streamKey.fields.join(item.streamKey.delimiter || '.') : '—',
          },
          {
            id: 'status',
            header: 'Status',
            sortingField: 'status',
            cell: (item: StoreItem) => {
              switch (item.status) {
                case 'ACTIVE':
                  return <StatusIndicator type="success">Active</StatusIndicator>;
                case 'DELETED':
                  return <StatusIndicator type="error">Deleted</StatusIndicator>;
                case 'ARCHIVED':
                  return <StatusIndicator type="info">Archived</StatusIndicator>;
                default:
                  return <StatusIndicator type="warning">{item.status}</StatusIndicator>;
              }
            },
          },
        ]}
        empty={
          <Box textAlign="center" padding="l">
            <SpaceBetween size="m">
              <b>No event stores</b>
              <Box variant="p" color="text-body-secondary">
                You haven&apos;t created any event stores in this region.
              </Box>
              <Button onClick={() => navigate('/stores/create')}>
                Create event store
              </Button>
            </SpaceBetween>
          </Box>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={pageTokens.current.length}
            openEnd={!!nextToken}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={{ pageSize, visibleContent: visibleColumns }}
            pageSizePreference={{
              title: 'Page size',
              options: [
                { value: 10, label: '10 stores' },
                { value: 25, label: '25 stores' },
                { value: 50, label: '50 stores' },
                { value: 100, label: '100 stores' },
              ],
            }}
            visibleContentPreference={{
              title: 'Visible columns',
              options: [
                {
                  label: 'Store properties',
                  options: [
                    { id: 'name', label: 'Name' },
                    { id: 'domain', label: 'Domain' },
                    { id: 'createdAt', label: 'Created' },
                    { id: 'updatedAt', label: 'Updated' },
                    { id: 'deletionProtection', label: 'Deletion protection' },
                    { id: 'streamKey', label: 'Stream key' },
                    { id: 'status', label: 'Status' },
                  ],
                },
              ],
            }}
            onConfirm={({ detail }) => {
              setPageSize(detail.pageSize ?? 50);
              if (detail.visibleContent) setVisibleColumns([...detail.visibleContent]);
              setCurrentPageIndex(1);
              pageTokens.current = [undefined];
            }}
          />
        }
        visibleColumns={visibleColumns}
        variant="full-page"
        stickyHeader={true}
      />
    </SpaceBetween>
  );
}
